import type { ErrorPolicy } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { ExportRowsRequest } from '@nudool/design-system';
import type { DocumentNode } from 'graphql';
import type * as React from 'react';
import { useEffect, useRef } from 'react';

import { createExportRowsFetcher } from './create-export-rows-fetcher.js';
import type { ListTableConnection } from './list-table-connection.js';
import { useExportConfirmDialog } from './use-export-confirm-dialog.js';

type UseExportRowsFetcherOptions<TData, TNode> = {
  query: DocumentNode;
  // Row-selection variables only, with no cursor -- fetchAllPages owns `first`/`after`.
  variables: Record<string, unknown>;
  extractConnection: (data: TData | undefined) => ListTableConnection<TNode> | undefined;
  totalCount: number | undefined;
  errorPolicy?: ErrorPolicy;
};

/** Page-walks the full filtered dataset for export, aborting with an error if the on-screen variables change mid-export. */
export function useExportRowsFetcher<TData, TNode>({
  query,
  variables,
  extractConnection,
  totalCount,
  errorPolicy,
}: UseExportRowsFetcherOptions<TData, TNode>): {
  onRequestExportRows: (options?: ExportRowsRequest) => Promise<TNode[] | null>;
  confirmDialog: React.ReactNode;
} {
  const client = useApolloClient();
  const { confirmLargeExport, confirmDialog } = useExportConfirmDialog();
  const latestVariablesRef = useRef(variables);

  useEffect(() => {
    latestVariablesRef.current = variables;
  });

  // Built per call, not per render, so the ref is only read once an export is running.
  function onRequestExportRows(options?: ExportRowsRequest) {
    return createExportRowsFetcher<TData, TNode>({
      client,
      query,
      variables,
      extractConnection,
      totalCount,
      errorPolicy,
      confirmLargeExport,
      readLatestVariables: () => latestVariablesRef.current,
    })(options);
  }

  return { onRequestExportRows, confirmDialog };
}
